import React, { useMemo, useState } from 'react';
import { useStripe, useElements, CardNumberElement } from '@stripe/react-stripe-js';
import {
    StripeCardCvcElementChangeEvent,
    StripeCardExpiryElementChangeEvent,
    StripeCardNumberElement,
    StripeCardNumberElementChangeEvent,
    StripeCardNumberElementOptions,
} from '@stripe/stripe-js';
import { defaultCssVariables } from 'core/styled';

type CardStatus = {
    error: string | null;
    complete: boolean;
};

export const useStripeFormVM = (isLoading: boolean | undefined) => {
    const stripe = useStripe();
    const elements = useElements();
    const options: StripeCardNumberElementOptions = useMemo(
        () => ({
            style: {
                invalid: {
                    color: defaultCssVariables.color.bloodyMary,
                },
            },
        }),
        [],
    );

    const [termsAndConditions, setTermsAndConditions] = useState<boolean>(false);

    const [numberStatus, setNumberStatus] = useState<CardStatus>({
        error: null,
        complete: false,
    });
    const [expiryStatus, setExpiryStatus] = useState<CardStatus>({
        error: null,
        complete: false,
    });
    const [cvcStatus, setCvcStatus] = useState<CardStatus>({
        error: null,
        complete: false,
    });

    const setStatusMap = {
        number: (status: CardStatus) => setNumberStatus(status),
        expiry: (status: CardStatus) => setExpiryStatus(status),
        cvc: (status: CardStatus) => setCvcStatus(status),
    };

    const handleTermsAndConditionsChange = () => {
        setTermsAndConditions(!termsAndConditions);
    };

    const handleChange =
        (field: keyof typeof setStatusMap) =>
        (
            event:
                | StripeCardNumberElementChangeEvent
                | StripeCardExpiryElementChangeEvent
                | StripeCardCvcElementChangeEvent,
        ): void => {
            setStatusMap[field]({ error: event.error?.message || null, complete: event.complete });
        };

    const handleSubmit =
        (handleStripePaymentSubmit: (stripePaymentMethodId: string | undefined) => void) =>
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (!stripe || !elements) {
                // Stripe.js has not loaded yet. Make sure to disable
                // form submission until Stripe.js has loaded.
                return;
            }

            const payload = await stripe.createPaymentMethod({
                type: 'card',
                card: elements.getElement(CardNumberElement) as StripeCardNumberElement,
            });

            handleStripePaymentSubmit(payload.paymentMethod?.id);
        };

    return {
        termsAndConditions,
        canSubmit:
            stripe &&
            numberStatus.complete &&
            expiryStatus.complete &&
            cvcStatus.complete &&
            !isLoading &&
            termsAndConditions,
        options,
        numberStatus,
        expiryStatus,
        cvcStatus,
        handleChange,
        handleSubmit,
        handleTermsAndConditionsChange,
    };
};
